import { useMemo } from 'react';
import { Link, useLoaderData, useSearchParams } from '@remix-run/react';
import {
    useAppDisplayModeContext,
    Grid,
    Flex,
    useStyles,
    MagazineTile,
    Caption,
    pxToRem,
    Image,
} from '@archipro-design/aria';
import type { AppDisplayMode, AppDisplayModeType } from '@archipro-design/aria';
import * as S from './MagazineCard.style';
import type { MagazineDirectoryTileViewModel } from '~/modules/magazine/type';
import { useTracker } from '@archipro-website/tracker';
import { decodeHTMLString } from '~/modules/seo/util';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import type { loader } from '~/routes/_app.digital-magazines._index';

interface MagazineCardProps {
    magazine: MagazineDirectoryTileViewModel;
}

type MagazineType =
    | 'Dedicated EDM'
    | 'ArchiPros Talk'
    | 'Curated Collection'
    | 'Commercial Edit'
    | 'Project Feature'
    | 'Misc';

// TODO: Add export on aira
export interface MagazineTileVariables {
    rootWidth: string;
    rootHeight: string;
    backgroundColor: string;
    imageHeight: string;
    imageWidth: string;
    color: string;
    subtitleSpacing?: string;
    titleSpacing?: string;
    titleMinHeight?: string;
}

export const MagazineCard = ({ magazine }: MagazineCardProps) => {
    const appDisplayMode = useAppDisplayModeContext();
    const tracker = useTracker();

    const {
        id,
        link,
        date,
        type,
        imageSrc,
        title,
        subtitle,
        formattedTitleHTML,
        frameStyle,
    } = magazine;

    const { year, month } = useLoaderData<typeof loader>();

    const variables = getVariableByType(type, appDisplayMode);

    const showLegacyThumbnails = frameStyle === 'Frameless';

    const [searchParams] = useSearchParams();
    const magazineLink = useMemo(() => {
        const newParams = new URLSearchParams();
        for (const param of ['page', 'sort']) {
            const paramValue = searchParams.get(param);
            if (paramValue) {
                newParams.append(param, paramValue);
            }
        }
        if (year) {
            newParams.append('year', year);
        }
        if (month) {
            newParams.append('month', month);
        }
        return `${link}?${newParams.toString()}`;
    }, [link, searchParams, year, month]);

    const { css } = useStyles<{ tileVars: MagazineTileVariables }>({
        tileVars: variables,
    });

    return (
        <Grid columns={'100%'} className={css(S.Card)}>
            <div className={css(S.TileContainer)}>
                {showLegacyThumbnails ? (
                    <Link
                        to={magazineLink}
                        preventScrollReset
                        className={css(S.TileLegacyThumbnail)}
                        prefetch="intent"
                        onClick={() => {
                            tracker.log('ArticleDigitalMagazineClick', {
                                url: new URL(window.location.href),
                                targetTracker: 'archiproTracker',
                                data: {
                                    // MagazineID
                                    ExtraData: id,
                                },
                            });
                        }}
                    >
                        <Image
                            src={imageSrc}
                            width={variables.rootWidth}
                            height={variables.rootHeight}
                            alt={decodeHTMLString(formattedTitleHTML)}
                            layout="fill"
                            sizes={getImagesSizes({
                                desktop: 424,
                                mobile: 204,
                            })}
                        />
                    </Link>
                ) : (
                    <MagazineTile
                        image={{
                            src: imageSrc,
                            width: variables.imageWidth,
                            height: variables.imageHeight,
                            alt: decodeHTMLString(formattedTitleHTML),
                            sizes: getImagesSizes({
                                desktop: 402,
                                mobile: 188,
                            }),
                        }}
                        subtitle={{
                            dangerouslySetInnerHTML: {
                                __html: subtitle,
                            },
                            variables: {
                                fontSize: pxToRem(
                                    appDisplayMode.desktop ? 36 : 18
                                ),
                                lineHeight: 1,
                            },
                            className: css(S.Subtitle),
                        }}
                        title={{
                            dangerouslySetInnerHTML: {
                                __html: formattedTitleHTML,
                            },
                            variables: {
                                fontSize: pxToRem(
                                    appDisplayMode.desktop ? 48 : 24
                                ),
                                letterSpacing: '-0.02em',
                                lineHeight: 1.1,
                            },
                            className: css(S.Title),
                        }}
                        variables={variables}
                        as={Link}
                        to={magazineLink}
                        preventScrollReset
                        prefetch={'intent'}
                        design={{
                            padding: pxToRem(appDisplayMode.desktop ? 16 : 8),
                        }}
                        onClick={() => {
                            tracker.log('ArticleDigitalMagazineClick', {
                                url: new URL(window.location.href),
                                targetTracker: 'archiproTracker',
                                data: {
                                    // MagazineID
                                    ExtraData: id,
                                },
                            });
                        }}
                    />
                )}
            </div>
            <Flex column={true} className={css(S.CardContent)} as="h2">
                <Caption
                    as={Link}
                    to={magazineLink}
                    link={true}
                    preventScrollReset
                    prefetch={'intent'}
                    variant={appDisplayMode.desktop ? '01' : '04'}
                    maximumLines={2}
                >
                    {title}
                </Caption>
                <Caption variant={appDisplayMode.desktop ? '01' : '04'}>
                    {date}
                </Caption>
            </Flex>
        </Grid>
    );
};

const getVariableByType = (
    type: string,
    appDisplayMode: AppDisplayModeType
): MagazineTileVariables => {
    const key = (Object.keys(appDisplayMode) as AppDisplayMode[]).find(
        (key) => appDisplayMode[key] === true
    ) as AppDisplayMode;

    const configs = {
        desktop: {
            rootWidth: 434,
            rootHeight: 724,
            imageWidth: 402,
            imageHeight: 476,
            titleMinHeight: 96,
        },

        mobile: {
            rootWidth: 204,
            rootHeight: 340,
            imageWidth: 188,
            imageHeight: 222,
            titleMinHeight: 52,
        },
    };
    const rootWidth = configs[key].rootWidth;
    const rootHeight = configs[key].rootHeight;
    const imageWidth = configs[key].imageWidth;
    const imageHeight = configs[key].imageHeight;
    const titleMinHeight = configs[key].titleMinHeight;

    let scheme: Pick<MagazineTileVariables, 'color' | 'backgroundColor'>;

    switch (type as MagazineType) {
        case 'ArchiPros Talk':
            scheme = {
                backgroundColor: '#8E8580',
                color: '#E5E5E5',
            };
            break;

        case 'Commercial Edit':
            scheme = {
                backgroundColor: '#D4D1CC',
                color: '#343434',
            };
            break;

        case 'Curated Collection':
            scheme = {
                backgroundColor: '#000000',
                color: '#E5E5E5',
            };
            break;

        case 'Project Feature':
        case 'Dedicated EDM':
        default:
            scheme = {
                backgroundColor: '#989485',
                color: '#E5E5E5',
            };
            break;
    }

    return {
        rootWidth: pxToRem(rootWidth),
        rootHeight: pxToRem(rootHeight),
        imageWidth: pxToRem(imageWidth),
        imageHeight: pxToRem(imageHeight),
        titleMinHeight: pxToRem(titleMinHeight),
        color: scheme.color,
        backgroundColor: scheme.backgroundColor,
    };
};
